import BnumElementInternal from '../../bnum-element-states';
import {
	EMPTY_STRING,
	isNullOrUndefined,
	MayBe,
	Nullable,
} from '@rotomeca/utils';
import { TAG_DATE } from '../../../core/utils/constants/tags';
import { JsCircularEvent } from '@rotomeca/event';
import { CustomDom } from '../../bnum-element';
import { Define, Self } from '../../../core/decorators';
import { Result, Risky } from '@rotomeca/rop';
import Log from '../../../core/utils/Log';
import {
	BnumDateLocale,
	BnumDateUtils,
} from '../../../core/utils/BnumDateUtils';
import {
	ATTRIBUTE_DATE,
	ATTRIBUTE_FORMAT,
	ATTRIBUTE_LOCALE,
	ATTRIBUTE_START_FORMAT,
	DEFAULT_FORMAT,
	DEFAULT_LOCALE,
	EVENT_ATTRIBUTE_UPDATED,
	EVENT_DATE,
	STATE_INVALID,
	STATE_NOT_READY,
} from './bnum-date.internals/constants';
import { Listener, NoInitListener } from 'core/decorators/listener';
import { Observe } from 'core/decorators/observe';
import { BnumDateCreateOptions } from './bnum-date.internals/types';

/**
 * Affiche une date formatée qui peut être mise à jour dynamiquement.
 *
 * /!\ Seuls les formats de date supportés ceux par intl.DateTimeFormat.
 *
 * Vous DEVEZ utiliser les tokens suivants pour la configuration du format en html:
 *
 * - P : format court (ex: 12/08/1997)
 * - PP : format moyen (ex: 12 août 1997)
 * - PPP : format long (ex: mardi 12 août 1997)
 * - PPPP : format complet (ex: mardi 12 août 1997)
 * - yyyy : année sur 4 chiffres
 * - yy : année sur 2 chiffres
 * - M : mois numérique sans zéro initial
 * - MM : mois numérique avec zéro initial
 * - MMM : mois abrégé (ex: août)
 * - MMMM : mois complet (ex: août)
 * - d : jour du mois sans zéro initial
 * - dd : jour du mois avec zéro initial
 * - EEEE : jour de la semaine complet (ex: mardi)
 * - E : jour de la semaine abrégé (ex: mar)
 * - H : heure sans zéro initial (0-23)
 * - HH : heure avec zéro initial (00-23)
 * - mm : minutes avec zéro initial (00-59)
 *
 * Pour la locale, utilisez ceux par intl.
 *
 * A la place de `fr_FR`, vous pouvez utilisez `fr`.
 *
 * @structure Date simple
 * <bnum-date format="P">1997-08-12</bnum-date>
 *
 * @structure Date avec parsing personnalisé
 * <bnum-date format="PPPP" data-start-format="dd/MM/yyyy">12/08/1997</bnum-date>
 *
 * @structure Date avec attribut data-date
 * <bnum-date format="ddMMyyyy HHmm" data-date="1997-08-12T15:30:00Z"></bnum-date>
 *
 * @structure Date en anglais
 * <bnum-date format="PPPP" locale="en">1997-08-12</bnum-date>
 *
 * @state invalid - Actif quand la date est invalide ou non définie
 * @state not-ready - Actif quand le composant n'est pas encore prêt
 */
@Define({ tag: TAG_DATE })
@Observe(ATTRIBUTE_FORMAT, ATTRIBUTE_LOCALE)
export class HTMLBnumDate extends BnumElementInternal {
	//#region Constants
	static readonly #_LOCALES: Record<string, BnumDateLocale> = {
		fr: BnumDateLocale.FR,
		en: BnumDateLocale.EN,
	};
	//#endregion Constants

	//#region Private fields
	/** Référence à la classe HTMLBnumDate */
	@Self _!: typeof HTMLBnumDate;

	/** L'objet Date (notre source de vérité) */
	#_originalDate: Nullable<Date> = null;

	/** Le format d'affichage (ex: 'PPPP') */
	#_outputFormat: string = DEFAULT_FORMAT; // 'P' -> 12/08/1997

	/** La locale (code) */
	#_locale: string = DEFAULT_LOCALE;

	/** Le format de parsing (ex: 'dd/MM/yyyy') */
	#_startFormat: Nullable<string> = null;

	/** L'élément SPAN interne qui contient le texte formaté */
	#_outputElement: Nullable<HTMLSpanElement> = null;
	#_renderSheduled: boolean = false;
	#_renderFrameId: MayBe<number>;
	//#endregion Private fields

	//#region Getter/Setters

	/**
	 * Événement circulaire déclenché lors du formatage de la date.
	 * Permet de personnaliser le formatage via un listener externe.
	 */
	@Listener(NoInitListener, { circular: true })
	accessor formatEvent!: JsCircularEvent<
		(param: { date: string | Date }) => { date: string | Date }
	>;

	/**
	 * Définit ou obtient l'objet Date.
	 * C'est le point d'entrée principal pour JS.
	 */
	get date(): Date | null {
		return this.#_originalDate;
	}
	set date(value: Date | string | null) {
		this.setDate(value, this.#_startFormat, true);
	}

	/** Définit ou obtient le format d'affichage. */
	get format(): string {
		return this.#_outputFormat;
	}
	set format(value: string) {
		this.setAttribute(ATTRIBUTE_FORMAT, value);
	}

	/** Définit ou obtient la locale. */
	get locale(): string {
		return this.#_locale;
	}
	set locale(value: string) {
		this.setAttribute(ATTRIBUTE_LOCALE, value);
	}

	public get localeElement(): BnumDateLocale {
		return (this._.#_LOCALES[this.#_locale] ||
			this.#_locale ||
			this._.#_LOCALES[DEFAULT_LOCALE]) as BnumDateLocale;
	}

	//#endregion Getters/Setters

	//#region Lifecycle
	constructor() {
		super();
	}

	/**
	 * Construit le DOM interne (appelé une seule fois).
	 * @param container Le ShadowRoot
	 */
	protected _p_buildDOM(container: CustomDom): void {
		this.#_outputElement = document.createElement('span');
		this.#_outputElement.setAttribute('part', 'date-text'); // Permet de styler depuis l'extérieur
		container.append(this.#_outputElement);
	}

	protected _p_detach(): void {
		if (!isNullOrUndefined(this.#_renderFrameId)) {
			cancelAnimationFrame(this.#_renderFrameId!);
			this.#_renderFrameId = null;
		}
	}

	/**
	 * Phase de pré-chargement (avant _p_buildDOM).
	 * On lit les attributs initiaux et le textContent.
	 */
	protected _p_preload(): void {
		// On ajoute un listener sur `bnum-date:attribute-updated` pour trigger les propriété de manière + précises.
		this.addEventListener(EVENT_ATTRIBUTE_UPDATED, e => {
			this.trigger(
				`${EVENT_ATTRIBUTE_UPDATED}:${(e as CustomEvent).detail.property}`,
				(e as CustomEvent).detail,
			);
		});

		// Lire les attributs de configuration
		this.#_outputFormat =
			this.getAttribute(ATTRIBUTE_FORMAT) || this.#_outputFormat;
		this.#_locale = this.getAttribute(ATTRIBUTE_LOCALE) || this.#_locale;
		this.#_startFormat = this.getAttribute(ATTRIBUTE_START_FORMAT) || null;

		// Déterminer la date initiale (priorité à data-date)
		const initialDateStr =
			this.getAttribute(ATTRIBUTE_DATE) || this.textContent?.trim() || null;

		// Définir la date sans déclencher de rendu (render=false)
		if (initialDateStr) this.setDate(initialDateStr, this.#_startFormat, false);
	}

	/**
	 * Phase d'attachement (après _p_buildDOM).
	 * C'est ici qu'on fait le premier rendu.
	 */
	protected _p_attach(): void {
		this.#_renderDate();
	}

	/**
	 * Gère les changements d'attributs (appelé après _p_preload).
	 */
	protected _p_update(
		name: string,
		oldVal: string | null,
		newVal: string | null,
	): void {
		if (oldVal === newVal) return;

		let needsRender = false;

		switch (name) {
			case ATTRIBUTE_FORMAT:
				this.#_outputFormat = newVal || DEFAULT_FORMAT;
				needsRender = true;
				break;
			case ATTRIBUTE_LOCALE:
				this.#_locale = newVal || DEFAULT_LOCALE;
				needsRender = true;
				break;
			case ATTRIBUTE_START_FORMAT:
				this.#_startFormat = newVal;
				// Pas de re-rendu, affecte seulement le prochain setDate()
				break;
			case ATTRIBUTE_DATE:
				// Re-parse la date
				this.setDate(newVal, this.#_startFormat, false);
				needsRender = true;
				break;
		}

		if (needsRender) {
			this.#_renderDate();
			// On déclenche l'événement pour la réactivité
			this.trigger(EVENT_ATTRIBUTE_UPDATED, {
				property: name,
				newValue: newVal,
				oldValue: oldVal,
			});
		}
	}
	//#endregion Lifecycle

	//#region Public Methods
	/**
	 * Définit la date à partir d'une chaîne, d'un objet Date ou null.
	 * @param dateInput La date source.
	 * @param startFormat Le format pour parser la date si c'est une chaîne.
	 * @param triggerRender Indique s'il faut rafraîchir l'affichage (par défaut: true).
	 */
	public setDate(
		dateInput: string | Date | null,
		startFormat?: string | null,
		triggerRender: boolean = true,
	): void {
		const oldDate = this.#_originalDate;
		let newDate: Date | null = null;

		if (dateInput === null) {
			newDate = null;
		} else if (dateInput instanceof Date) {
			newDate = dateInput;
		} else if (typeof dateInput === 'string') {
			if (dateInput.trim() === 'now') {
				newDate = new Date();
			} else {
				const formatToUse = startFormat || this.#_startFormat;
				if (formatToUse) {
					// Parsing avec format spécifique
					newDate = BnumDateUtils.parse(dateInput, formatToUse); //parse(dateInput, formatToUse, new Date());
				} else {
					// Parsing natif (ISO 8601, timestamps...)
					newDate = new Date(dateInput);
				}
			}
		}

		// Vérification de la validité
		if (newDate && BnumDateUtils.isValid(newDate)) {
			this.#_originalDate = newDate;
		} else {
			this.#_originalDate = null;
		}

		// Déclenche le rendu et/ou l'événement si la date a changé
		if (oldDate?.getTime() !== this.#_originalDate?.getTime()) {
			if (triggerRender) {
				this.#_renderDate();
			}
			this.trigger(EVENT_DATE, {
				property: 'date',
				newValue: this.#_originalDate,
				oldValue: oldDate,
			});
		}
	}

	/** Récupère l'objet Date actuel. */
	public getDate(): Date | null {
		return this.#_originalDate;
	}

	/** Ajoute un nombre de jours à la date actuelle. */
	public addDays(days: number): void {
		if (!this.#_originalDate) return;
		this.date = BnumDateUtils.addDays(this.#_originalDate, days);
	}

	/** Ajoute un nombre de mois à la date actuelle. */
	public addMonths(months: number): void {
		if (!this.#_originalDate) return;
		this.date = BnumDateUtils.addMonths(this.#_originalDate, months);
	}

	/** Ajoute un nombre d'années à la date actuelle. */
	public addYears(years: number): void {
		if (!this.#_originalDate) return;
		this.date = BnumDateUtils.addYears(this.#_originalDate, years);
	}

	public askRender(): void {
		if (this.#_renderSheduled) return;
		this.#_renderSheduled = true;
		this.#_renderFrameId = requestAnimationFrame(() => {
			this.#_renderFrameId = null;
			this.#_renderSheduled = false;
			this.#_renderDate();
		});
	}

	//#endregion Public Methods

	//#region Private Methods
	/**
	 * Met à jour le textContent du span interne.
	 * C'est la seule fonction qui écrit dans le DOM.
	 */
	#_renderDate(): void {
		this._p_clearStates();
		if (!this.#_outputElement) {
			this._p_addState(STATE_NOT_READY);
			return; // Pas encore prêt
		}

		if (!this.#_originalDate) {
			this.#_outputElement.textContent = EMPTY_STRING; // Affiche une chaîne vide si date invalide/null
			this._p_addState(STATE_INVALID);
			return;
		}

		// Trouve la locale, avec fallback sur 'fr'
		const locale = this.localeElement;
		const textContent = this.#_format(locale).match({
			Ok: formated =>
				this.formatEvent.call({ date: formated })?.date || formated,
			Err: e => {
				Log.error(
					'HTMLBnumDate/renderDate',
					`Erreur de formatage Intl. Format: "${this.#_outputFormat}`,
					'\\',
					BnumDateUtils.getOptionsFromToken(this.#_outputFormat),
					'"',
					e,
				);

				this._p_addState(STATE_INVALID);
				return 'Date invalide';
			},
		});

		this.#_outputElement.textContent = textContent;
		this.setAttribute('aria-label', this.#_outputElement.textContent);
	}

	@Risky()
	#_format(locale: BnumDateLocale): Result<string> {
		if (this.#_originalDate === null) throw new Error('Date is null');

		return BnumDateUtils.format(
			this.#_originalDate,
			BnumDateUtils.getOptionsFromToken(this.#_outputFormat),
			locale,
		) as any;
	}

	//#endregion Private Methods

	//#region Statics
	/**
	 * Méthode statique pour la création (non implémentée ici,
	 * mais suit le pattern de BnumElement).
	 */
	static Create(
		dateInput?: string | Date,
		options?: BnumDateCreateOptions,
	): HTMLBnumDate {
		const el = (document.createElement(this.TAG) as HTMLBnumDate).condAttr(
			options?.startFormat as unknown as boolean,
			ATTRIBUTE_START_FORMAT,
			options?.startFormat ?? EMPTY_STRING,
		);
		if (options?.format) el.format = options.format;
		if (options?.locale) el.locale = options.locale;

		if (typeof dateInput === 'string')
			el.appendChild(document.createTextNode(dateInput));
		else if (dateInput) el.date = dateInput;

		return el;
	}

	//#endregion Statics
}
